<template>
  <div>
    <b-row class="ml-2">
      <b-button
        :disabled="isShowEarlyQuaddie"
        v-if="!isShowQuaddie && !isFutureRace"
        size="sm"
        variant="success"
        class="font-weight-bold"
        @click="handleShowQuaddie"
      >
        <feather-icon icon="ClockIcon" />
        Enable Quaddie
      </b-button>
      <b-button
        v-if="isShowQuaddie && items.some((i) => i.isLateQuaddie) && !isFutureRace"
        size="sm"
        variant="danger"
        class="font-weight-bold"
        @click="handleDisableQuaddie"
      >
        <feather-icon icon="XCircleIcon" />
        Disable Quaddie
      </b-button>

      <b-button
        :disabled="isShowQuaddie"
        v-if="!isShowEarlyQuaddie && !isFutureRace"
        size="sm"
        variant="primary"
        class="font-weight-bold ml-1"
        @click="handleShowEarlyQuaddie"
      >
        <feather-icon icon="ClockIcon" />
        Enable Early Quaddie
      </b-button>
      <b-button
        v-if="isShowEarlyQuaddie && !isFutureRace"
        size="sm"
        variant="danger"
        class="font-weight-bold ml-1"
        @click="handleDisableEarlyQuaddie"
      >
        <feather-icon icon="XCircleIcon" />
        Disable Early Quaddie
      </b-button>
      <b-button
        v-if="isShowQuaddie || isShowEarlyQuaddie"
        size="sm"
        variant="success"
        class="font-weight-bold ml-1"
        @click="handleSave"
        :disabled="selected.length !== 4"
      >
        <feather-icon icon="SaveIcon" />
        Save
      </b-button>
      <b-button
        v-if="isShowQuaddie || isShowEarlyQuaddie"
        size="sm"
        class="font-weight-bold ml-1"
        @click="handleCancel"
      >
        Cancel
      </b-button>
    </b-row>
    <p class="text-danger mt-1 font-weight-bold">
      * Crawling will be automatically disabled for any race that has changes.
    </p>
    <b-row>
      <b-col cols="12">
        <b-table
          :key="isSelected"
          show-empty
          :thead-class="'text-nowrap'"
          :items="items"
          :fields="fields"
          responsive
          bordered
          :busy="loadingRaces"
          :tbody-tr-class="rowClass"
          ref="selectableTable"
          :selectable="isSelected"
          @row-selected="onRowSelected"
          selected-variant="info"
        >
          <template #cell(select)="{ rowSelected }">
            <!-- <input type="checkbox" v-model="rowSelected" /> -->
            <template v-if="rowSelected">
              <span aria-hidden="true">&check;</span>
              <span class="sr-only">{{ rowSelected }}</span>
            </template>
            <template v-else>
              <span aria-hidden="true">&nbsp;</span>
              <span class="sr-only">Not selected</span>
            </template>
          </template>
          <template #cell(race)="{ item }">
            <div @click="redirectRace(item)" style="cursor: pointer">
              <span class="font-weight-bolder text-primary mr-1">
                R{{ item.eventNo }}
              </span>
              <b-badge
                v-if="item.raceId === currentRace.raceId"
                variant="primary"
              >
                CURRENT
              </b-badge>
            </div>
          </template>
          <template #cell(status)="{ item }">
            <b-badge :variant="statusColor(item.finalStatus)">
              {{ item.finalStatus }}
            </b-badge>
          </template>
          <template #cell(time)="{ item }">
            <span>
              {{ format(new Date(item.scheduledUtcTime), "hh:mm") }}
            </span>
          </template>
          <template #cell(runners)="{ item }">
            <span>
              {{ item.totalRunner }}
            </span>
          </template>
          <template #cell(liability)="{ item }">
            <span>
              {{ item.liability || "-" }}
            </span>
          </template>

          <template #cell(hold)="{ item }">
            <span>
              {{ formatCurrencyBuiltInFunc(item.hold) || "-" }}
            </span>
          </template>
          <template #cell(netResult)="{ item }">
            <span
              :class="{
                'text-success': item.netResult > 0,
                'text-danger': item.netResult < 0,
              }"
            >
              {{
                item.netResult ? formatCurrencyBuiltInFunc(item.netResult) : "-"
              }}
            </span>
          </template>
          <template #cell(isDisableSync)="{ item }">
            <b-media vertical-align="center">
              <div class="d-flex align-items-center text-nowrap">
                <b-form-checkbox
                  :checked="!item.isDisableSync"
                  @click.native.prevent="handleCheckboxClick(item)"
                  switch
                />
              </div>
            </b-media>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-row align-v="center">
          <b-col class="d-flex align-items-center">
            <h3>{{ currentRace.eventName }}</h3>
            <b-badge
              :variant="statusColor(currentRace.finalStatus)"
              class="ml-2"
              >{{ currentRace.finalStatus }}</b-badge
            >
            <b-badge variant="warning" class="ml-2">{{
              currentRace.distance
            }}</b-badge>
          </b-col>
          <b-col cols="12">
            <small class="text-muted">
              Start Time:
              {{ format(new Date(currentRace.scheduledUtcTime), "hh:mm a P") }}
              · Distance: {{ currentRace.distance }} · Grade:
              {{ currentRace.grade }} · Prize: {{currentRace.prize}}
            </small>
          </b-col>
        </b-row>

        <b-row>
          <b-col class="d-flex" style="gap: 8px">
            <!-- Change Status -->
            <b-dropdown size="sm" variant="outline-warning">
              <template #button-content>
                <feather-icon icon="SettingsIcon" class="text-warning" />
                Change Status
              </template>

              <b-dropdown-header class="border-bottom">
                <p class="mb-0">Change Status</p>
                <small class="text-muted"
                  >Current: {{ currentRace.status }}</small
                >
              </b-dropdown-header>

              <b-dropdown-item
                v-for="(item, index) in statusOptions"
                :key="index"
                @click="openChangeStatusModal(item)"
              >
                <div class="d-flex align-items-center" style="gap: 4px">
                  <feather-icon
                    :icon="item.icon"
                    :class="`text-${item.color}`"
                  />
                  <div>
                    <p class="mb-0">{{ item.title }}</p>
                    <small class="text-muted">{{ item.subtitle }}</small>
                  </div>
                </div>
              </b-dropdown-item>
            </b-dropdown>

            <!-- Reopen Race -->
            <b-dropdown size="sm" variant="outline-success">
              <template #button-content>
                <feather-icon icon="RefreshCcwIcon" class="text-success" />
                Reopen Race
              </template>

              <b-dropdown-header class="border-bottom">
                <p class="mb-0">Reopen Race</p>
                <small class="text-muted">Select duration</small>
              </b-dropdown-header>

              <b-dropdown-item
                v-for="(item, index) in reopenOptions"
                :key="index"
                @click="handleReopenTime(item)"
              >
                <div class="d-flex align-items-center" style="gap: 4px">
                  <feather-icon
                    :icon="item.icon"
                    :class="`text-${item.color}`"
                  />
                  <div>
                    <p class="mb-0">{{ item.title }}</p>
                    <small class="text-muted">{{ item.subtitle }}</small>
                  </div>
                </div>
              </b-dropdown-item>
            </b-dropdown>

            <!-- Buttons -->
            <b-button
              size="sm"
              variant="outline-primary"
              class="font-weight-bold"
              @click="handleOpenManualResultModal"
            >
              <feather-icon icon="FileTextIcon" />
              Manually Result
            </b-button>
          </b-col>
          <b-col
            cols="auto"
            class="d-flex flex-wrap justify-content-between"
            style="gap: 8px"
          >
            <b-button size="sm" variant="danger" @click="openAbandonRaceModal">
              <feather-icon icon="XCircleIcon" />
              Abandon Race</b-button
            >
            <b-button
              size="sm"
              variant="danger"
              @click="openAbandonMeetingModal"
            >
              <b-icon-building />
              Abandon Meeting</b-button
            >
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="redirectToEditRace"
              >Edit Race</b-button
            >
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="mt-1">
      <b-col cols="12">
        <Runners :silkStrip="currentRace.silkStrip" />
      </b-col>
    </b-row>

    <b-row class="mt-4">
      <b-col cols="12">
        <b-card><BetsOnRace /></b-card>
      </b-col>
    </b-row>

    <ManualResultModal :meeting-races="items" :is-future="$route.query.type === 'future'"/>
    <AbandonRaceModal @onFetchRaces="fetchRaces" />
    <AbandonMeetingModal @onFetchRaces="fetchRaces" />
    <ChangeStatusModal :status="selectedStatus" @onRefetch="fetchRaces" />
    <DisabledRaceModal @onAfterConfirm="handleDisableConfirmed" />
    <!-- MODAL CONFIRM -->
    <b-modal
      ref="modal"
      :title="modal.title"
      centered
      cancel-variant="outline-secondary"
      :ok-disabled="isSubmitting"
      @ok="handleSubmitModal"
    >
      <b-card-text style="color: black">
        {{ modal.content }}
      </b-card-text>
      <template #modal-ok>
        <Spinner v-if="isSubmitting" />
        <span v-else>Submit</span>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BPagination,
  BRow,
  BButtonGroup,
  BTable,
  BCardBody,
  BFormInput,
  BMedia,
  BAvatar,
  BLink,
  BContainer,
  BFormCheckbox,
  BFormCheckboxGroup,
  BBadge,
  BDropdown,
  BDropdownItemButton,
  BDropdownItem,
  BDropdownHeader,
  BIconBuilding,
  BCardText,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import ManualResultModal from "./modals/ManualResultModal.vue";
import AbandonRaceModal from "./modals/AbandonRaceModal.vue";
import AbandonMeetingModal from "./modals/AbandonMeetingModal.vue";
import ChangeStatusModal from "./modals/ChangeStatusModal.vue";
import DisabledRaceModal from "./modals/DisabledRaceModal.vue";
import { mapState, mapActions } from "vuex";
import { addMinutes, format } from "date-fns";
import BetsOnRace from "./BetsOnRace.vue";
import Runners from "./Runners.vue";
import useToastHandler from "@/services/toastHandler";
import Spinner from "@/@core/components/Spinner.vue";

export default {
  components: {
    BDropdown,
    BDropdownItemButton,
    BBadge,
    BContainer,
    BButton,
    BCard,
    BCol,
    BPagination,
    BRow,
    BButtonGroup,
    BTable,
    BCardBody,
    BFormInput,
    BMedia,
    BAvatar,
    BLink,
    vSelect,
    flatPickr,
    BFormCheckbox,
    BFormCheckboxGroup,
    FeatherIcon,
    BDropdownItem,
    BDropdownHeader,
    BIconBuilding,
    ManualResultModal,
    AbandonRaceModal,
    AbandonMeetingModal,
    ChangeStatusModal,
    BetsOnRace,
    Runners,
    BCardText,
    Spinner,
    DisabledRaceModal,
  },
  data() {
    const statusOptions = [
      {
        icon: "PlayIcon",
        color: "success",
        title: "Open",
        value: "OPEN",
        subtitle: "Betting is open",
      },
      {
        icon: "SquareIcon",
        color: "danger",
        title: "Close",
        value: "CLOSED",
        subtitle: "Betting is closed",
      },
      {
        icon: "ClockIcon",
        color: "danger",
        title: "Interim",
        value: "INTERIM",
        subtitle: "Unofficial results",
      },
      {
        icon: "CheckCircleIcon",
        color: "success",
        title: "Final",
        value: "FINISHED",
        subtitle: "Final results confirmed",
      },
    ];
    const reopenOptions = [
      {
        icon: "ClockIcon",
        color: "success",
        title: "2 Minutes",
        value: 2,
        subtitle: "Reopen for 2 minutes",
      },
      {
        icon: "ClockIcon",
        color: "primary",
        title: "5 Minutes",
        value: 5,
        subtitle: "Reopen for 5 minutes",
      },
      {
        icon: "ClockIcon",
        color: "danger",
        title: "10 Minutes",
        value: 10,
        subtitle: "Reopen for 10 minutes",
      },
      {
        icon: "RefreshCcwIcon",
        color: "muted",
        title: "Indefinitely",
        value: null,
        subtitle: "Reopen until manually closed",
      },
    ];

    return {
      payouts: true,
      global: true,
      fixed: true,
      statusOptions,
      reopenOptions,
      selectedStatus: null,
      fields: [
        { key: "select" },
        { key: "race", label: "Race" },
        { key: "time", label: "Time" },
        { key: "status", label: "Status" },
        { key: "runners", label: "Runners" },
        { key: "liability", label: "Liability" },
        { key: "hold", label: "Hold" },
        { key: "netResult", label: "Net Result" },
        { key: "isDisableSync", label: "Crawl Status" },
      ],
      items: [],
      runners: [],
      currentRace: null,
      action: {
        type: "",
        item: "",
      },
      modal: {
        title: "",
        content: "",
      },
      selected: [],
      isSelected: false,
      isShowQuaddie: false,
      isShowEarlyQuaddie: false,
      pendingAction: null,
      pendingPayload: null,
    };
  },
  computed: {
    ...mapState("trading/racing", ["loadingRaces", "loadingUpdateRaceDetail"]),
    isSubmitting() {
      return this.loadingUpdateRaceDetail;
    },
    isFutureRace() {
      return this.$route.query.type === "future";
    },
  },
  watch: {
    "$route.query.raceId"(newRaceId) {
      this.fetchRaces();
      this.getDetailOfRace(this.$route.query.raceId);
    },
    "$route.query.type"(val, oldVal) {
      if (val !== oldVal) this.fetchRaces();
    },
  },
  created() {
    this.fetchRaces();
    this.getDetailOfRace(this.$route.query.raceId);
  },
  methods: {
    ...mapActions("trading/racing", [
      "getRacesOfMeeting",
      "updateRaceDetail",
      "getDetailOfRace",
    ]),
    format,
    formatCurrencyBuiltInFunc,
    async fetchRaces() {
      const type = this.$route.query.type;
      const data = await this.getRacesOfMeeting({
        meetingId: this.$route.params.id,
        ...(type && { type }),
      });
      this.items = data;
      this.currentRace =
        this.items.find((race) => race.raceId === this.$route.query.raceId) ||
        {};
      this.runners = data[0]?.results || [];
      // this.isSelected = false
    },
    statusColor(status) {
      switch (status) {
        case "FINAL":
          return "success";
        case "UPCOMING":
          return "warning";
        case "OPEN":
          return "primary";
        case "ABANDONED":
          return "danger";
        default:
          return "secondary";
      }
    },
    handleOpenManualResultModal() {
      // if (!this.currentRace.isDisableSync) {
      //   this.pendingAction = "CHANGE_MANUAL_RESULT";
      //   this.$bvModal.show("disabled-race-modal");
      //   return;
      // }
      this.$bvModal.show("manual-result-modal");
    },
    openAbandonRaceModal() {
      if (!this.currentRace.isDisableSync) {
        this.pendingAction = "OPEN_ABANDON_RACE";
        this.$bvModal.show("disabled-race-modal");
        return;
      }
      this.$bvModal.show("abandon-race-modal");
    },
    openAbandonMeetingModal() {
      if (!this.currentRace.isDisableSync) {
        this.pendingAction = "OPEN_ABANDON_MEETING";
        this.$bvModal.show("disabled-race-modal");
        return;
      }
      this.$bvModal.show("abandon-meeting-modal");
    },
    openChangeStatusModal(item) {
      if (!this.currentRace.isDisableSync) {
        this.selectedStatus = item.value;
        this.pendingAction = "CHANGE_STATUS";
        this.$bvModal.show("disabled-race-modal");
        return;
      }
      this.selectedStatus = item.value;
      this.$bvModal.show("change-status-modal");
    },
    redirectToEditRace() {
      const meetingId = this.$route.params.id;
      const raceId = this.$route.query.raceId;
      const query = { raceId };
      if (this.$route.query.type) query.type = this.$route.query.type;
      this.$router.push({
        name: "trading-racing-edit",
        params: { id: meetingId },
        query,
      });
    },
    rowClass(item) {
      if (!item) return;

      if (item.raceId === this.currentRace.raceId) {
        return "current-race-row";
      }
    },
    handleCheckboxClick(item) {
      this.showModal("crawl", item);
    },
    setModal(title, content) {
      this.modal.title = title;
      this.modal.content = content;
    },

    showModal(type, item) {
      this.action = { item, type };
      if (type === "crawl") {
        this.setModal(
          "Update Status Crawl",
          `Are you sure you want to ${
            item.isDisableSync ? "enable" : "disable"
          } crawl status?`
        );
      }
      if (type === "disable-quaddie") {
        this.setModal(
          "Disable Quaddie",
          "Are you sure you want to disable bet quaddie for this meeting?"
        );
      }

      if (type === "disable-early-quaddie") {
        this.setModal(
          "Disable Early Quaddie",
          "Are you sure you want to disable bet early quaddie for this meeting?"
        );
      }

      this.$refs.modal.show();
    },

    async handleSubmitModal(event) {
      event.preventDefault();
      const { item, type } = this.action;

      if (type === "crawl") await this.handleChangeStatusCrawl(item);
      if (type === "disable-quaddie") await this.handleDisable(item);
      if (type === "disable-early-quaddie") await this.handleDisableEarly(item);

      if (!this.isSubmitting) {
        this.$nextTick(() => {
          this.$refs.modal.hide();
        });
      }
    },
    async handleChangeStatusCrawl(item) {
      const result = await this.updateRaceDetail({
        raceId: item.raceId,
        isDisableSync: !item.isDisableSync,
      });
      if (result) {
        this.showToastSuccess(true, "Updated crawl status successfully!");
        this.fetchRaces();
      }
    },
    onRowSelected(items) {
      this.selected = items;
    },
    handleShowQuaddie() {
      this.isShowQuaddie = !this.isShowQuaddie;
      this.isSelected = true;
      const listQuaddie = this.items.filter((i) => i.isLateQuaddie);

      this.$nextTick(() => {
        if (this.$refs.selectableTable) {
          if (listQuaddie.length > 0) {
            listQuaddie.forEach((i) => {
              this.$refs.selectableTable.selectRow(i.eventNo - 1);
            });
          }
        }
      });
    },
    handleShowEarlyQuaddie() {
      this.isShowEarlyQuaddie = !this.isShowEarlyQuaddie;
      this.isSelected = true;
      const listEarlyQuaddie = this.items.filter((i) => i.isEarlyQuaddie);

      this.$nextTick(() => {
        if (this.$refs.selectableTable) {
          if (listEarlyQuaddie.length > 0) {
            listEarlyQuaddie.forEach((i) => {
              this.$refs.selectableTable.selectRow(i.eventNo - 1);
            });
          }
        }
      });
    },
    handleDisableQuaddie() {
      this.showModal("disable-quaddie");
    },
    async handleDisable() {
      const listDisable = this.items.filter((i) => i.isLateQuaddie);
      const racePromises = listDisable.map(async (item) => {
        return this.updateRaceDetail({
          raceId: item.raceId,
          ...(!item.isDisableSync && { isDisableSync: true }),
          isLateQuaddie: !item.isLateQuaddie,
        }).then((res) => {
          return true;
        });
      });

      const res = await Promise.all(racePromises);
      if (!res.some((i) => !i)) {
        this.showToastSuccess(true, "Disable bet quaddie successfully!");
        this.fetchRaces();
        this.handleCancel();
      }
    },
    handleDisableEarlyQuaddie() {
      this.showModal("disable-early-quaddie");
    },
    async handleDisableEarly() {
      const listDisable = this.items.filter((i) => i.isEarlyQuaddie);
      const racePromises = listDisable.map(async (item) => {
        return this.updateRaceDetail({
          raceId: item.raceId,
          ...(!item.isDisableSync && { isDisableSync: true }),
          isEarlyQuaddie: !item.isEarlyQuaddie,
        }).then((res) => {
          return true;
        });
      });

      const res = await Promise.all(racePromises);
      if (!res.some((i) => !i)) {
        this.showToastSuccess(true, "Disable bet quaddie successfully!");
        this.fetchRaces();
        this.handleCancel();
      }
    },
    handleCancel() {
      this.isShowEarlyQuaddie = false;
      this.isShowQuaddie = false;
      this.isSelected = false;
      this.$refs.selectableTable.clearSelected();
    },
    handleSave() {
      if (this.selected.length !== 4)
        return this.showToastError(false, "Please select 4 races to proceed.");

      if (
        !this.selected.every((item, index) => {
          if (index === 0) return true;
          return (
            Number(item.eventNo) ===
            Number(this.selected[index - 1].eventNo) + 1
          );
        })
      )
        return this.showToastError(false, "Please select 4 consecutive races.");

      if (this.isShowQuaddie) return this.handleSaveQuaddie();
      if (this.isShowEarlyQuaddie) return this.handleSaveEarlyQuaddie();
    },
    async handleSaveQuaddie() {
      const itemEarlyQuaddie = this.items.find((i) => i.isEarlyQuaddie);
      if (
        !!itemEarlyQuaddie &&
        this.selected[0].eventNo === itemEarlyQuaddie.eventNo
      )
        return this.showToastError(
          false,
          "The Quaddie list cannot be the same as the Early Quaddie list."
        );
      const listNeedToChange = this.selected.filter((i) => !i.isLateQuaddie);
      const listDisableEarlyQuaddie = this.items
        .filter((i) => i.isLateQuaddie)
        .filter((i) => !this.selected.some((l) => l.eventNo === i.eventNo));
      const newList = [...listNeedToChange, ...listDisableEarlyQuaddie];
      const racePromises = newList.map(async (item) => {
        return this.updateRaceDetail({
          raceId: item.raceId,
          ...(!item.isDisableSync && { isDisableSync: true }),
          isLateQuaddie: !item.isLateQuaddie,
        }).then((res) => {
          return true;
        });
      });

      const res = await Promise.all(racePromises);
      if (!res.some((i) => !i)) {
        this.showToastSuccess(true, "Updated quaddie list successfully!");
        this.fetchRaces();
        this.handleCancel();
      }
    },
    async handleSaveEarlyQuaddie() {
      const itemQuaddie = this.items.find((i) => i.isLateQuaddie);
      if (!!itemQuaddie && this.selected[0].eventNo === itemQuaddie.eventNo)
        return this.showToastError(
          false,
          "The Early Quaddie list cannot be the same as the Quaddie list."
        );

      const listNeedToChange = this.selected.filter((i) => !i.isEarlyQuaddie);
      const listDisableQuaddie = this.items
        .filter((i) => i.isEarlyQuaddie)
        .filter((i) => !this.selected.some((l) => l.eventNo === i.eventNo));
      const newList = [...listNeedToChange, ...listDisableQuaddie];
      const racePromises = newList.map(async (item) => {
        return this.updateRaceDetail({
          raceId: item.raceId,
          ...(!item.isDisableSync && { isDisableSync: true }),
          isEarlyQuaddie: !item.isEarlyQuaddie,
        }).then((res) => {
          return true;
        });
      });

      const res = await Promise.all(racePromises);
      if (!res.some((i) => !i)) {
        this.showToastSuccess(true, "Updated early quaddie list successfully!");
        this.fetchRaces();
        this.handleCancel();
      }
    },
    async handleReopenTime(item) {
      if (!this.currentRace.isDisableSync) {
        this.pendingAction = "REOPEN_RACE";
        this.pendingPayload = item;
        this.$bvModal.show("disabled-race-modal");
        return;
      }
      await this.reopenRace(item);
    },
    async reopenRace(item) {
      const payload = {
        raceId: this.currentRace.raceId,
        finalStatus: "REOPEN",
      };
      const isExistReopenStatus = this.currentRace.finalStatus === "REOPEN";
      if (isExistReopenStatus) delete payload.finalStatus;
      if (item.value) {
        payload.reopenTime = addMinutes(new Date(), item.value).toISOString();
      } else {
        payload.reopenTime = new Date(2099, 11, 31).toISOString();
      }
      const result = await this.updateRaceDetail(payload);
      if (result) {
        this.showToastSuccess("success", "Reopened race successfully!");
        await this.fetchRaces();
      }
    },
    async handleDisableConfirmed() {
      this.currentRace.isDisableSync = true;
      switch (this.pendingAction) {
        case "REOPEN_RACE":
          await this.reopenRace(this.pendingPayload);
          break;
        case "CHANGE_STATUS":
          this.$bvModal.show("change-status-modal");
          break;
        case "OPEN_ABANDON_RACE":
          this.$bvModal.show("abandon-race-modal");
          break;
        case "OPEN_ABANDON_MEETING":
          this.$bvModal.show("abandon-meeting-modal");
          break;
        case "CHANGE_MANUAL_RESULT":
          this.$bvModal.show("manual-result-modal");
          break;
        default:
          break;
      }
      this.pendingAction = null;
      this.pendingPayload = null;
    },
    redirectRace(race) {
      if (!race) return;
      const query = { raceId: race.raceId };
      if (this.$route.query.type) query.type = this.$route.query.type;
      this.$router.push({
        name: "trading-racing-view",
        params: { id: this.$route.params.id },
        query,
      });
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();

    return {
      showToastError,
      showToastSuccess,
    };
  },
};
</script>
<style lang="scss">
.current-race-row {
  background-color: #e7f1ff !important;
}
</style>
